/**
 * /cotizacion/:token — Página pública de cotización para el cliente.
 * Sin nav de admin, sin login. Muestra el estado de la solicitud y permite aprobar.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { CheckCircle2, Clock, AlertCircle, Loader2, ArrowLeft } from "lucide-react";
import { dbSmGetMerchantQuoteByToken } from "@/lib/db-service";
import type { MerchantQuoteWithItems } from "@/lib/db-service/merchant";
import { dbSmGetTenantConfig } from "@/lib/db-service";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

// ─── SERVER FN ────────────────────────────────────────────────────────────────

interface CotizacionPageData {
  quote: MerchantQuoteWithItems | null;
  tenantName: string;
  tenantSlug: string | null;
  tenantColor: string | null;
  tenantLogoUrl: string | null;
}

const fetchCotizacion = createServerFn({ method: "GET" })
  .inputValidator((token: string) => token)
  .handler(async ({ data: token }): Promise<CotizacionPageData> => {
    const quote = await dbSmGetMerchantQuoteByToken(token);
    if (!quote) return { quote: null, tenantName: "", tenantSlug: null, tenantColor: null, tenantLogoUrl: null };

    const config = await dbSmGetTenantConfig(quote.tenantId).catch(() => null);
    return {
      quote,
      tenantName: (config as { name?: string } | null)?.name ?? quote.tenantId,
      tenantSlug: (config as { slug?: string } | null)?.slug ?? null,
      tenantColor: (config as { primaryColor?: string } | null)?.primaryColor ?? null,
      tenantLogoUrl: (config as { logoUrl?: string } | null)?.logoUrl ?? null,
    };
  });

// ─── ROUTE ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/cotizacion/$token")({
  loader: async ({ params }) => fetchCotizacion({ data: params.token }),
  component: CotizacionPage,
  head: () => ({
    meta: [
      { title: "Tu cotización — Structura" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

// ─── COUNTDOWN ────────────────────────────────────────────────────────────────

function Countdown({ validUntil }: { validUntil: string }) {
  const [remaining, setRemaining] = useState(() => {
    const diff = new Date(validUntil).getTime() - Date.now();
    return Math.max(0, diff);
  });

  useEffect(() => {
    if (remaining <= 0) return;
    const interval = setInterval(() => {
      const diff = new Date(validUntil).getTime() - Date.now();
      setRemaining(Math.max(0, diff));
    }, 1000);
    return () => clearInterval(interval);
  }, [validUntil]);

  if (remaining <= 0) return <span className="text-red-500 font-semibold">Vencida</span>;

  const totalSecs = Math.floor(remaining / 1000);
  const d = Math.floor(totalSecs / 86400);
  const h = Math.floor((totalSecs % 86400) / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;

  return (
    <span className="font-mono font-bold text-blue-700">
      {d > 0 ? `${d}d ` : ""}{String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
    </span>
  );
}

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    pending_quote: { label: "Pendiente de cotización", variant: "secondary" },
    quoted:        { label: "Cotizado", variant: "default" },
    approved:      { label: "Aprobado", variant: "default" },
    rejected:      { label: "Rechazado", variant: "destructive" },
    expired:       { label: "Vencido", variant: "outline" },
    recotizar:     { label: "Solicitud de recotización", variant: "outline" },
  };
  const info = map[status] ?? { label: status, variant: "secondary" as const };
  return <Badge variant={info.variant}>{info.label}</Badge>;
}

// ─── PAGE ─────────────────────────────────────────────────────────────────────

function CotizacionPage() {
  const loaderData = Route.useLoaderData() as CotizacionPageData;
  const { quote, tenantName, tenantSlug, tenantColor, tenantLogoUrl } = loaderData;
  const rawParams = Route.useParams() as Record<string, string>;
  const token = rawParams.token ?? "";
  const [approving, setApproving] = useState(false);
  const [approveSuccess, setApproveSuccess] = useState(false);
  const [recotizando, setRecotizando] = useState(false);
  const [recotizaSuccess, setRecotizaSuccess] = useState(false);

  const color = tenantColor ?? "#2563eb";

  if (!quote) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-700 mb-2">Cotización no encontrada</h1>
          <p className="text-gray-500">El link puede haber expirado o ser inválido.</p>
        </div>
      </div>
    );
  }

  const isExpired =
    quote.validUntil ? new Date(quote.validUntil).getTime() < Date.now() : false;

  async function handleApprove() {
    setApproving(true);
    try {
      await fetch(`/api/merchant/quotes/${quote!.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-Id": quote!.tenantId,
        },
        body: JSON.stringify({ status: "approved" }),
      });
      setApproveSuccess(true);
    } finally {
      setApproving(false);
    }
  }

  async function handleRecotizar() {
    setRecotizando(true);
    try {
      await fetch(`/api/merchant/quotes/${quote!.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-Id": quote!.tenantId,
        },
        body: JSON.stringify({ status: "recotizar" }),
      });
      setRecotizaSuccess(true);
    } finally {
      setRecotizando(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header style={{ background: color }} className="border-b border-black/10">
        <div className="max-w-xl mx-auto px-4 h-16 flex items-center gap-3">
          {tenantLogoUrl ? (
            <img src={tenantLogoUrl} alt={tenantName} className="h-8 object-contain" />
          ) : (
            <span className="font-bold text-white text-xl">{tenantName}</span>
          )}
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-8 space-y-6">
        {/* Estado */}
        <div className="flex items-center gap-3">
          <StatusBadge status={quote.status} />
          <span className="text-sm text-gray-400">
            {new Date(quote.createdAt).toLocaleDateString("es-AR", { day: "2-digit", month: "long", year: "numeric" })}
          </span>
        </div>

        {/* Solicitud */}
        <Card className="p-5">
          <h2 className="font-bold text-gray-900 mb-4">Tu solicitud</h2>
          <div className="flex items-center gap-3 mb-4">
            <div>
              <p className="font-semibold text-gray-800">{quote.productName}</p>
              <p className="text-sm text-gray-500">Cantidad: {quote.quantity}</p>
            </div>
          </div>

          {quote.items.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Zonas configuradas</p>
              {quote.items.map((item) => (
                <div key={item.id} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                  {item.fileUrl && (
                    <img
                      src={item.fileUrl}
                      alt={item.zoneName}
                      className="w-12 h-12 rounded object-cover border border-gray-200 shrink-0"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-800">{item.zoneName}</p>
                    <Badge variant="secondary" className="text-xs mt-0.5">{item.workTypeName}</Badge>
                    {item.description && <p className="text-xs text-gray-500 mt-1">{item.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {quote.notes && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Notas</p>
              <p className="text-sm text-gray-700">{quote.notes}</p>
            </div>
          )}
        </Card>

        {/* Cotización */}
        {quote.status === "pending_quote" && (
          <Card className="p-5 border-amber-200 bg-amber-50">
            <div className="flex items-center gap-2 mb-2">
              <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
              <h2 className="font-bold text-amber-800">Tu solicitud fue recibida</h2>
            </div>
            <p className="text-sm text-amber-700">
              Estamos revisando tu solicitud. Te contactaremos a la brevedad con el precio y los detalles.
            </p>
          </Card>
        )}

        {quote.status === "quoted" && (
          <Card className="p-5 border-blue-200">
            <h2 className="font-bold text-gray-900 mb-4">Cotización</h2>

            {/* Precio destacado */}
            <div className="text-center py-4 mb-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total</p>
              <p className="text-5xl font-black text-gray-900">
                ${quote.quotedPrice?.toLocaleString("es-AR", { minimumFractionDigits: 2 }) ?? "—"}
              </p>
              {quote.quantity > 1 && quote.quotedPrice && (
                <p className="text-sm text-gray-500 mt-1">
                  ${(quote.quotedPrice / quote.quantity).toLocaleString("es-AR", { minimumFractionDigits: 2 })} por unidad
                </p>
              )}
            </div>

            {/* Vigencia */}
            {quote.validUntil && (
              <div className={`flex items-center gap-2 p-3 rounded-lg mb-4 ${isExpired ? "bg-red-50 border border-red-200" : "bg-blue-50 border border-blue-200"}`}>
                <Clock className={`w-4 h-4 shrink-0 ${isExpired ? "text-red-400" : "text-blue-400"}`} />
                <div>
                  <p className="text-xs text-gray-500">Vigencia</p>
                  {isExpired
                    ? <Badge variant="destructive" className="text-xs">Cotización vencida</Badge>
                    : <Countdown validUntil={quote.validUntil} />
                  }
                </div>
              </div>
            )}

            {/* Plazo */}
            {quote.leadTimeDays && (
              <p className="text-sm text-gray-600 mb-4">
                Plazo de producción estimado: <strong>{quote.leadTimeDays} días hábiles</strong>
              </p>
            )}

            {/* Notas */}
            {quote.quoteNotes && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-xs font-semibold text-gray-500 mb-1">Comentarios</p>
                <p className="text-sm text-gray-700">{quote.quoteNotes}</p>
              </div>
            )}

            {approveSuccess ? (
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-4">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <p className="text-sm text-green-700 font-medium">¡Pedido confirmado! Ya estamos trabajando en tu solicitud.</p>
              </div>
            ) : isExpired ? (
              recotizaSuccess ? (
                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <CheckCircle2 className="w-5 h-5 text-blue-500" />
                  <p className="text-sm text-blue-700">Recibimos tu pedido de recotización. Te avisaremos pronto.</p>
                </div>
              ) : (
                <Button
                  onClick={handleRecotizar}
                  disabled={recotizando}
                  variant="outline"
                  className="w-full"
                >
                  {recotizando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Solicitar recotización
                </Button>
              )
            ) : (
              <Button
                onClick={handleApprove}
                disabled={approving}
                className="w-full text-white font-bold py-3 text-base"
                style={{ background: color }}
              >
                {approving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Aprobar y pagar
              </Button>
            )}
          </Card>
        )}

        {quote.status === "approved" && (
          <Card className="p-5 border-green-200 bg-green-50">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-bold text-green-800">¡Pedido confirmado!</p>
                <p className="text-sm text-green-700">Ya estamos trabajando en tu solicitud.</p>
              </div>
            </div>
          </Card>
        )}

        {quote.status === "recotizar" && (
          <Card className="p-5 border-amber-200 bg-amber-50">
            <div className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
              <div>
                <p className="font-bold text-amber-800">Recotización en proceso</p>
                <p className="text-sm text-amber-700">Recibimos tu pedido. Te enviaremos la nueva cotización pronto.</p>
              </div>
            </div>
          </Card>
        )}

        {/* Volver a la tienda */}
        {tenantSlug && (
          <Link
            to="/market/$slug"
            params={{ slug: tenantSlug }}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a la tienda
          </Link>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 text-center border-t border-gray-200 mt-8">
        <p className="text-xs text-gray-400">
          Powered by{" "}
          <a href="https://structura.com.ar" className="hover:underline">Structura</a>
        </p>
      </footer>
    </div>
  );
}
